/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : updateForm.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { ComponentRegistry } from "./registry";
import { PAGE_SIZES } from "./pageSize";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { Box, Grid, IconButton } from "@mui/material";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import customConsole from "../../../config/customConsole";
import { HandleApiActions } from "../../../redux/actions";
import { useDispatch } from "react-redux";
import { CustomLogsDialog } from "../../../components";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AttributeUpdateDialog from "../../labcar/Attributes/AttributeUpdateDialog";

/* ---------------- COMPONENT ---------------- */
export default function UpdateFormRenderer({
  isOpen,
  onClose,
  data,
  setDynamicFormData,
  setDynamicFormFilteredData,
  selectedComponent,
  componentType,
}) {
  /* ---------------- STATE ---------------- */
  const [template, setTemplate] = useState(data || { fields: [] });
  const pageRefs = useRef([]);
  const [renderVersion, setRenderVersion] = useState(0);
  const [openTemplateChangeLogsDialog, setOpenTemplateChangeLogsDialog] =
    useState(false);
  const [templateChangeLogsList, setTemplateChangeLogsList] = useState([]);
  const [templateLogsDialogData, setTemplateLogsDialogData] = useState({});
  const [isEdited, setIsEdited] = useState(false);
  const [reason, setReason] = useState("");
  const [isPdfMode, setIsPdfMode] = useState(false);
  const [openAttributeUpdateDialog, setOpenAttributeUpdateDialog] =
    useState(false);

  const dispatch = useDispatch();
  /* =====================================================
     FIX 3: INITIALIZE INITIAL VALUES IMMEDIATELY
     (NO undefined, EVEN ON FIRST RENDER)
  ===================================================== */
  const initialValuesRef = useRef(
    Object.fromEntries(
      (data?.fields ?? []).flatMap((page, pageIndex) =>
        (page.elements ?? []).map((el) => [
          `${pageIndex}_${el.id}`,
          el?.properties?.value ?? "",
        ]),
      ),
    ),
  );

  /* ---------- SYNC TEMPLATE WHEN DATA CHANGES ---------- */
  useEffect(() => {
    if (!data) return;

    // Update template
    setTemplate(data);

    // Update initial values (still no re-render needed)
    initialValuesRef.current = Object.fromEntries(
      (data.fields ?? []).flatMap((page, pageIndex) =>
        (page.elements ?? []).map((el) => [
          `${pageIndex}_${el.id}`,
          el?.properties?.value ?? "",
        ]),
      ),
    );
  }, [data]);

  const getSelectedComponentType = () => {
    console.log("---componentType---", componentType, selectedComponent);
    if (componentType) return String(componentType).toLowerCase();
    if (selectedComponent?.swm_id) return "swm";
    if (selectedComponent?.ee_id) return "ee";
    if (selectedComponent?.vc_id) return "vc";
    if (selectedComponent?.vs_id) return "vs";
    return "vc";
  };
  const updateElements = (elements, elementId, value) => {
    return elements.map((el) => {
      if (el.id === elementId) {
        return {
          ...el,
          properties: {
            ...el.properties,
            value,
          },
        };
      }
      return el;
    });
  };
  const updatePages = (fields, pageIndex, elementId, value) => {
    return fields.map((page, pIdx) => {
      if (pIdx !== pageIndex) return page;

      return {
        ...page,
        elements: updateElements(page.elements, elementId, value),
      };
    });
  };

  const updateElementValue = useCallback((pageIndex, elementId, value) => {
    setIsEdited(true);

    setTemplate((prev) => ({
      ...prev,
      fields: updatePages(prev.fields, pageIndex, elementId, value),
    }));
  }, []);

  /* ---------- SAVE ---------- */
  const handleSave = () => {
    updateFieldValues();
  };

  const updateFieldValues = async () => {
    let apiDetails = typeApprovalAPI.update_dynamic_form;
    if (template && template?.form_id) {
      customConsole({
        ...apiDetails,
        params: {
          form_id: Number(template?.form_id),
          field_details: template.fields,
        },
        show_toast: true,
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            form_id: Number(template?.form_id),
            field_details: template.fields,
            template_tag: "NEW",
            reason: reason,
          },
          show_toast: true,
        }),
      );
      if (!resp?.error) {
        // IMPORTANT: use UPDATED template, not old data
        initialValuesRef.current = Object.fromEntries(
          (template.fields ?? []).flatMap((page, pageIndex) =>
            (page.elements ?? []).map((el) => [
              `${pageIndex}_${el.id}`,
              el?.properties?.value ?? "",
            ]),
          ),
        );
        setRenderVersion((v) => v + 1);
        setIsEdited(false);

        // Optional: success log
        console.log("Initial values updated after save");
      }
    }

  };

  /* Get Templates change logs */
  const handleGetTemplateChangeLogs = async ({ pageNo, pageSize }) => {
    let apiDetails = typeApprovalAPI.get_dynamic_form_logs;

    if (template && template?.form_id) {
      customConsole({
        ...apiDetails,
        params: {
          form_id: Number(template?.form_id),
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            form_id: Number(template?.form_id),
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      );
      if (resp.error) {
        setTemplateChangeLogsList([]);
      } else {
        setTemplateChangeLogsList(resp?.data);
      }
    }
  };

  const handleTemplateLogsDialogOpen = (data) => {
    setOpenTemplateChangeLogsDialog(true);

    setTemplateLogsDialogData(data);
  };
  const handleTemplateLogsDialogClose = () => {
    setTemplateLogsDialogData(null);
    setTemplateChangeLogsList([]);

    setOpenTemplateChangeLogsDialog(false);
  };
  /* ---------- RENDER ---------- */

  const downloadPDF = async () => {
    let pdf = null;

    try {
      // Enable PDF mode (React state)
      setIsPdfMode(true);

      // Wait for React to re-render DOM
      await new Promise((resolve) => setTimeout(resolve, 150));

      for (let i = 0; i < template.fields.length; i++) {
        const page = template.fields[i];
        const ref = pageRefs.current[i];
        if (!ref) continue;

        let pxW = PAGE_SIZES[page.settings.pageSize]?.width || 794;
        let pxH = PAGE_SIZES[page.settings.pageSize]?.height || 1123;

        if (page.settings.orientation === "Landscape") {
          [pxW, pxH] = [pxH, pxW];
        }

        const mmW = (pxW * 25.4) / 96;
        const mmH = (pxH * 25.4) / 96;

        const canvas = await html2canvas(ref, {
          scale: window.devicePixelRatio * 2,
          useCORS: true,
          backgroundColor: page.settings.backgroundColor || "#fff",
        });

        const imgData = canvas.toDataURL("image/png", 1.0);

        if (!pdf) {
          pdf = new jsPDF(
            page.settings.orientation === "Landscape"
              ? "landscape"
              : "portrait",
            "mm",
            [mmW, mmH],
          );
        } else {
          pdf.addPage([mmW, mmH]);
        }

        const imgProps = pdf.getImageProperties(imgData);
        const pdfHeight = (imgProps.height * mmW) / imgProps.width;

        pdf.addImage(imgData, "PNG", 0, 0, mmW, pdfHeight, undefined, "FAST");
      }

      pdf?.save(`${template.name || "form"}.pdf`);
    } finally {
      // Restore UI mode
      setIsPdfMode(false);
    }
  };

  console.log(reason, "reason,reason");
  return (
    <>
      <Grid container>
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
            gap: 2,
          }}
        >
          <Box>
            {isEdited && (
              <CustomFormTextFieldV2
                label="Enter Reason"
                isMultiline
                placeholder="Enter Reason"
                value={reason}
                txtFldStyle={{ width: 380 }}
                setValue={setReason}
                required={true}
              />
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "end",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CustomButtonV2
                type="submit"
                size="small"
                btnName="Update"
                disabled={!isEdited || reason.trim() === ""}
                handleOnClick={handleSave}
              />
              <CustomButtonV2
                type="other"
                size="small"
                btnName="Log"
                handleOnClick={(e) =>
                  handleTemplateLogsDialogOpen({ pageNo: 1, pageSize: 25 })
                }
              />
              <AddButtonV2 icon="download" handleOnClick={downloadPDF} />

              <IconButton
                onClick={() => {
                  console.log("--selectedComponent---", selectedComponent);
                  if (selectedComponent) {
                    setOpenAttributeUpdateDialog(true);
                  }
                }}
                disabled={!selectedComponent}
                aria-label="copy attributes"
              >
                <VisibilityOutlinedIcon />
              </IconButton>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {template.fields?.map((page, pageIndex) => {
            let pxW = PAGE_SIZES[page.settings.pageSize]?.width || 794;
            let pxH = PAGE_SIZES[page.settings.pageSize]?.height || 1123;

            if (page.settings.orientation === "Landscape") {
              [pxW, pxH] = [pxH, pxW];
            }

            return (
              <div
                key={pageIndex}
                ref={(el) => (pageRefs.current[pageIndex] = el)}
                style={{
                  position: "relative",
                  width: pxW,
                  height: pxH,
                  margin: "20px auto",
                  background: page.settings.backgroundColor,
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  //   boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                }}
              >
                {page.elements.map((el) => {
                  const reg = ComponentRegistry[el.type];
                  if (!reg?.render) return null;

                  const initialValue =
                    initialValuesRef.current[`${pageIndex}_${el.id}`];

                  return (
                    <div
                      key={el.id}
                      style={{
                        position: "absolute",
                        top: el.y,
                        left: el.x,
                        width: el.width,
                        height: el.height,
                      }}
                    >
                      {reg.render(
                        el,
                        el.properties?.value,
                        (v) => updateElementValue(pageIndex, el.id, v),
                        initialValue,
                        isPdfMode,
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Grid>
      </Grid>

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openAttributeUpdateDialog}
        setOpenAttributeUpdateDialog={setOpenAttributeUpdateDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        onClickSuccess={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        selectedComponent={selectedComponent}
        componentType={getSelectedComponentType()}
        type="COPY"
        hideDefaultActions
      />

      <CustomLogsDialog
        open={openTemplateChangeLogsDialog}
        handleClose={handleTemplateLogsDialogClose}
        dialogTitle={"Change Logs"}
        dialogContent={templateChangeLogsList}
        data={templateLogsDialogData}
        getLogsData={handleGetTemplateChangeLogs}
      />
    </>
  );
}

/* ---------- PAYLOAD MAPPER ---------- */
const mapIdToMasterTemplateId = (template) => {
  if (!template) return template;

  const {
    id,
    name,
    type,
    template_tag,
    fields,
    headers,
    footers,
    signatures,
    status,
  } = template;

  return {
    master_template_id: id,
    name,
    type,
    template_tag,
    fields,
    headers,
    footers,
    signatures,
    status,
  };
};
